import React, { useState, useEffect } from "react";
import { Button } from "./../../../shadcn/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../shadcn/components/ui/dialog";
import { Checkbox } from "../../../shadcn/components/ui/checkbox";
import { useSelector } from "react-redux";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { homeLoanInterest } from "services";
import AcknowledgementCard from "../../../shadcn/components/custom/AcknowledgementCard";
import { Card } from "../../../shadcn/components/ui/card";

interface HomeLoanCardPropsInterface {
  variant?: "legacy" | "pd";
}

export default function HomeLoanCard({
  variant = "legacy",
}: HomeLoanCardPropsInterface) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acknowledgementOpen, setAcknowledgementOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { user } = useSelector((state: any) => state.auth);
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const mobileNumber =
    user?.attributes?.find((number: any) => number?.type === "mobile_number")
      ?.value || "";
  const processedNumber = mobileNumber.startsWith("+91")
    ? mobileNumber.slice(3)
    : "";
  const form = useBetaForm({
    name: onboardingData?.name || "",
    mobile: processedNumber,
  });

  useEffect(() => {
    if (open) {
      form.setField("name", onboardingData?.name || "");
      form.setField("mobile", processedNumber);
    }
  }, [open]);

  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;

  const form_constraints = {
    name: {
      presence: { allowEmpty: false },
    },
    mobile: {
      presence: { allowEmpty: false },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    checked: {
      inclusion: {
        within: [true],
        message: "^You must agree to be contacted.",
      },
    },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [acknowledgementOpen]);

  const clearForm = () => {
    form.setField("name", "");
    form.setField("mobile", "");
    setIsChecked(false);
  };

  const handleSubmit = async () => {
    form.setField("checked", isChecked);
    if (form.validate()) {
      setLoading(true);
      let payload: any = {
        name: form.getField("name"),
      };
      let mobile = form.getField("mobile");
      if (mobile.match(+91)) {
        payload["mobile"] = mobile;
      } else {
        payload["mobile"] = `+91${mobile}`;
      }
      try {
        await homeLoanInterest(payload);
      } catch (error) {
        console.log("error: ", error);
      }

      setAcknowledgementOpen(true);
      setLoading(false);
    }
  };

  return (
    <HomeLoanCardWrapper variant={variant}>
      <div
        className={`flex justify-between items-center ${
          variant === "pd" ? "" : "px-3 py-4"
        }`}
      >
        <div className="text-brand-bgBlack font-openSans text-base font-semibold leading-normal">
          Want to explore loan options for this property?
        </div>
        <Dialog
          open={open}
          onOpenChange={(e: boolean) => {
            setOpen(e);
            setAcknowledgementOpen(false);
            if (!e) clearForm();
          }}
        >
          <DialogTrigger asChild>
            <Button>Know more</Button>
          </DialogTrigger>
          <DialogContent className="max-w-[400px] rounded-lg">
            <DialogTitle className="hidden"></DialogTitle>
            <DialogDescription className="hidden"></DialogDescription>
            {!acknowledgementOpen && (
              <div>
                <p className="font-openSans font-semibold text-center text-[24px] leading-8 text-black">
                  Enter Details
                </p>
                <form className="flex flex-col space-y-8 w-full mt-2">
                  <div className="space-y-2">
                    <label className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack">
                      Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Name"
                      value={form.getField("name")}
                      onChange={(e) => form.setField("name", e.target.value)}
                      className="bg-[#F8F8F8] border-none h-10 p-2 w-full font-openSans font-semibold text-web-body-sm leading-4 text-[#A1A0A0]"
                    />
                    {form.errors.get("name") && (
                      <p className="mt-2 text-sm text-red-500">
                        {form.errors.get("name")[0]}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      placeholder="+91"
                      value={form.getField("mobile")}
                      onChange={(e) => form.setField("mobile", e.target.value)}
                      className="bg-[#F8F8F8] border-none h-10 outline-none p-2 w-full font-openSans font-semibold text-web-body-sm leading-4 text-[#A1A0A0]"
                    />
                    {form.errors.get("mobile") && (
                      <p className="mt-2 text-sm text-red-500">
                        {form.errors.get("mobile")[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex items-start w-full gap-[10px]">
                    <div className="mt-2">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={
                          (value) => setIsChecked(Boolean(value)) // Update state on change
                        }
                      />
                    </div>
                    <div className="font-openSans text-sm text-brand-textGrey">
                      I agree to be contacted through various channels for
                      updates of properties
                    </div>
                  </div>
                  {form.errors.get("checked") && (
                    <p className="mt-2 text-sm text-red-500">
                      {form.errors.get("checked")[0]}
                    </p>
                  )}
                </form>
                <Button
                  className="mt-6 w-full"
                  onClick={handleSubmit}
                  disabled={!isChecked || loading}
                >
                  Next
                </Button>
              </div>
            )}
            {acknowledgementOpen && (
              <AcknowledgementCard
                name={form.getField("name")}
                message={
                  "Our customer executive will reach out to you shortly to address your loan inquiries and guide you through the next steps seamlessly."
                }
                contactInfo={{
                  mobileNumber: form.getField("mobile"),
                }}
                hideBottomActions={true}
                hideEmailSection={true}
                handleInterested={() => {}}
                handleNotInterested={() => {}}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </HomeLoanCardWrapper>
  );
}

const HomeLoanCardWrapper = ({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: HomeLoanCardPropsInterface["variant"];
}) => {
  if (variant === "pd") {
    return (
      <Card className="bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
        {children}
      </Card>
    );
  }

  return (
    <div className="max-w-[258px] bg-wave-pattern1 bg-no-repeat bg-cover w-full bg-[#F8F8F8] hover:shadow-lg hover:scale-[1.01] hover:shadow-300 hover:duration-300 hover:ease-out">
      {children}
    </div>
  );
};
